import sample from 'lodash.sample'
import { USER_TYPES, ACTIONS, GET_STARTED_ROUTES } from 'components/GetStarted/constants'
import { PeekabooExperiment } from 'components/experiments/peekaboo/usePeekaboo'
import { GetStarted } from 'graph/GetStarted'

export const GET_STARTED_RETURN_URL_COOKIES_KEY = 'getStartedReturnUrl'

export const getHeadingForUserType = ({
  userType,
  providerHeading: provider,
  seekerHeading: seeker,
  defaultHeading: fallback = '',
}: TODO) => {
  return getByUserType({ userType, provider, seeker, fallback })
}

export const getByUserType = ({ userType, provider, seeker, fallback }: TODO) => {
  const subject = userType === USER_TYPES.PROVIDER ? provider : seeker
  return userType ? subject : fallback
}

type PredicateConfig = {
  userInput?: any
  query?: any
  data: GetStarted
  experiments?: PeekabooExperiment[]
}

type Predicate = (config: PredicateConfig) => boolean

export const getStepRedirect = ({
  redirectIfTests = [],
  userInput,
  data,
  query,
  experiments = [],
}: {
  redirectIfTests: any
  userInput: any
  data: any
  query: any
  experiments: PeekabooExperiment[]
}) => {
  const { redirectTo } =
    redirectIfTests.find((item: TODO) =>
      item.test.every((predicate: Predicate) =>
        predicate({
          userInput,
          query,
          data,
          experiments,
        })
      )
    ) || {}
  const redirectPath = typeof redirectTo === 'function' ? redirectTo(data) : redirectTo
  const isUrlAbsolute = !Object.values(GET_STARTED_ROUTES).some((path) => path === redirectPath)
  return { redirectPath, isUrlAbsolute }
}

export const isLoggedIn: Predicate = ({ data }) => !!data?.viewer?.user

export const isAnonymousUser: Predicate = (config) => !isLoggedIn(config)

export const isSubscribed = ({ userInput, data }: TODO) =>
  !!data?.viewer?.isSubscribed || !!userInput?.isSubscribed

export const multipleListingsEnabled: Predicate = (config) =>
  isLoggedIn(config) &&
  config.data?.viewer?.site?.multipleSeekerListings &&
  config.data?.viewer?.user?.userType?.toUpperCase() === USER_TYPES.SEEKER

export const multipleListingsDisabled: Predicate = (config) => !multipleListingsEnabled(config)

export const hasActiveListing: Predicate = ({ data }) => !!data?.viewer?.hasActiveListing

export const hasNoActiveListing: Predicate = (config) => !hasActiveListing(config)

export const isSponsorshipDisabled: Predicate = ({ data }) =>
  !!data?.site?.supportsSponsorship === false

export const hasSelectedBasicPlan: Predicate = ({ userInput }) => userInput?.paymentPlanId === null

export const isProfileTypePrevStepIncomplete: Predicate = ({ userInput }) =>
  !!userInput?.userType === false

export const isContactInfoPrevStepIncomplete = isProfileTypePrevStepIncomplete

export const isInExperiment =
  (key: string, variant: string): Predicate =>
  ({ experiments }: TODO) => {
    const isInExperiment = experiments.some(
      (experiment: TODO) => experiment.key === key && experiment?.variation?.key === variant
    )
    return isInExperiment
  }

export const isNotInExperiment = (key: string, variant: string) => (config: TODO) => {
  return !isInExperiment(key, variant)(config)
}

export const isEnterNamePrevStepIncomplete: Predicate = ({ userInput, ...config }) =>
  isContactInfoPrevStepIncomplete({ userInput, ...config }) ||
  !!userInput.phoneNumber === false ||
  !!userInput.email === false

export const isCreateUserPrevStepIncomplete: Predicate = ({ userInput, ...config }) =>
  isEnterNamePrevStepIncomplete({ userInput, ...config }) ||
  !!userInput?.firstName === false ||
  !!userInput?.lastName === false

export const isSponsorshipPrevStepIncomplete: Predicate = ({ userInput, data }) => {
  const isCanadaOnly = !!data?.site?.canadaOnly
  const hasCountry = !!userInput?.country
  const hasState = !!userInput?.state
  const hasCity = !!userInput?.city
  return isCanadaOnly ? !hasState || !hasCity : !hasCountry || !hasState || !hasCity
}

export const isServicesPrevStepIncomplete: Predicate = ({ userInput, data, ...config }) => {
  const isPrevStepIncomplete = isSponsorshipDisabled({ userInput, data, ...config })
    ? false
    : typeof userInput?.sponsorship !== 'boolean'
  return !!(isSponsorshipPrevStepIncomplete({ userInput, data, ...config }) || isPrevStepIncomplete)
}

export const isRatePrevStepIncomplete: Predicate = (config) => {
  if (hasEmptyServices(config)) {
    return false
  }
  return isServicesPrevStepIncomplete(config) || !!config.userInput?.services === false
}

export const isDetailsPrevStepIncomplete: Predicate = (config) => {
  const isProvider = config.userInput?.userType === USER_TYPES.PROVIDER
  const isInvalidRate = isProvider ? false : !!config.userInput?.hourlyRate === false
  return isRatePrevStepIncomplete(config) || isInvalidRate
}

export const isPaymentPrevStepIncomplete: Predicate = ({ userInput, query }) =>
  !!userInput?.stripePlan === false && !!query?.stripePlanId === false

export const isValidUserType = (value: string) =>
  // @ts-ignore
  Object.keys(USER_TYPES).some((key) => value?.toUpperCase() === USER_TYPES[key])

export const hasUserTypeInUrl = ({ query }: TODO) => isValidUserType(query?.user_type as string)

export const userInputReducer = (state: TODO, action: TODO) => {
  switch (action.type) {
    case ACTIONS.UPDATE_USER_TYPE_FROM_QUERY:
      return {
        ...state,
        userType: state.userType || action?.userType?.toUpperCase(),
      }
    case ACTIONS.UPDATE_USER_INPUT_FROM_DATA:
      return {
        ...state,
        userType: action?.data?.viewer?.user?.userType?.toUpperCase() || state.userType,
        firstName: action?.data?.viewer?.user?.firstName || state.firstName,
        lastName: action?.data?.viewer?.user?.lastName || state.lastName,
      }
    default:
      return {
        ...state,
        ...action,
      }
  }
}

export const getFeaturesForStripePlan = ({
  showCnFeatures: isCanadianNanny,
  showCcaFeatures: isCCA,
  userType,
  isVip: isVip,
  quantity,
}: TODO) => {
  const SHARED_FEATURES = {
    TELESAFE: {
      label: 'Unlimited Protected Phone Calling with TELESAFE™',
      tooltip:
        'Securely send and receive calls on our platform without revealing your phone number.',
    },
    UNLIMITED_MESSAGES: { label: 'Unlimited Secure Private Message' },
  }
  const isProvider = userType === USER_TYPES.PROVIDER
  const isSeeker = userType === USER_TYPES.SEEKER

  if (isCanadianNanny) {
    const providerFeatures = isProvider ? [{ label: 'Featured as a Top Nanny Profile' }] : []
    const seekerFeatures = isSeeker ? [{ label: 'Featured Job Listing' }] : []
    const vipFeatures = isVip
      ? [
          { label: 'Payroll & Tax Service with Dedicated Payroll Consultant ($480 value)', isVip },
          { label: 'Candidate Background Check and Other Screening Services ($120 value)', isVip },
          {
            label:
              'Premium 1-Year Accounts on ElderCare.com, Housekeeper.com, PetSitter.com, Sitter.com, and more',
            isVip,
          },
          { label: 'Manage WSIB ($120 value)', isVip },
        ]
      : []
    return [
      ...providerFeatures,
      SHARED_FEATURES.UNLIMITED_MESSAGES,
      SHARED_FEATURES.TELESAFE,
      ...seekerFeatures,
      ...vipFeatures,
    ]
  }
  if (isCCA) {
    return [{ label: `${quantity} Active Job Posting${quantity > 1 ? 's' : ''} per Month` }]
  }
  // Default features
  return [
    SHARED_FEATURES.TELESAFE,
    SHARED_FEATURES.UNLIMITED_MESSAGES,
    { label: 'Featured Placement in Directory' },
  ]
}

export const getRandomFacetDescription = (facetNames: TODO = [], data: TODO) => {
  const randomFaceName = sample(facetNames) || {}
  const facets = data?.site?.facetQuestions?.[0]?.answerFacets || []
  return facets.find((facet: TODO) => facet.name === randomFaceName)?.description ?? ''
}

export const isThankYouStep = (step: TODO) => step?.urlSlug === GET_STARTED_ROUTES.THANKYOU

export const isCCA = (domain: string) => domain === 'childcareadvantage.com'

export const hasEmptyServices = ({ data }: TODO) => {
  const facets = data?.site?.facetQuestions?.[0]
  return !facets || facets.length === 0
}

export const hasEmptyStripePlans = ({ data }: TODO) => {
  const plans = data?.viewer?.stripePlans?.edges ?? []
  return plans.length === 0
}

export const getNextStepIndex = ({
  steps,
  currentPageIndex,
  data,
  userInput,
  experiments = [],
}: {
  steps: any
  currentPageIndex: number
  data: any
  userInput: any
  experiments: PeekabooExperiment[]
}) => {
  let nextStepIndex = currentPageIndex + 1
  let isNextStepAvailable = false

  // iterate through steps until a non-skipped step is found
  while (!isNextStepAvailable) {
    const nextStep = steps[nextStepIndex]
    const stepSkipIfTests = nextStep?.skipIf ?? []

    if (stepSkipIfTests.length === 0) {
      isNextStepAvailable = true
    }

    const isNextStepSkipped = stepSkipIfTests.some((testFn: TODO) =>
      testFn({ data, userInput, experiments })
    )
    if (!isNextStepSkipped) {
      isNextStepAvailable = true
    } else {
      nextStepIndex++
    }
  }

  return nextStepIndex
}

export const getStepIndexFromUrlSlug = (steps: TODO, stepUrlSlug: TODO) => {
  const index = steps.findIndex(({ urlSlug }: TODO) => urlSlug === stepUrlSlug)
  return index === -1 ? null : index
}

export const getLocationDropDownData = ({
  dependentField,
  dataFromSsr,
  dataFromUserInput,
  dataFromQuery,
  isLoading,
  hasDataChanged,
}: TODO) => {
  if (isLoading || !dependentField) {
    return []
  } else if (hasDataChanged) {
    return dataFromQuery || []
  } else if (dataFromUserInput) {
    return dataFromUserInput || []
  } else {
    return dataFromSsr || []
  }
}

export const getUserTimezone = () => {
  const isIntlDateTimeFormatSupported = !!(window.Intl && window.Intl.DateTimeFormat)
  return isIntlDateTimeFormatSupported ? Intl.DateTimeFormat().resolvedOptions().timeZone : null
}

export const getCityFromIpIfValidCountry = (data: TODO) => {
  const countryFromIp = data?.viewer?.currentCity?.state?.country?.countryId
  const supportedCountries = data?.site?.supportedCountries ?? []

  const isIpCountrySupported = supportedCountries.some((country: TODO) => {
    return country.countryId === countryFromIp
  })
  return isIpCountrySupported ? data?.viewer?.currentCity : null
}

export const durationInMonths = (duration: TODO) => {
  const durationMap = {
    month: 1,
    year: 12,
    quarter: 3,
  }

  // @ts-ignore
  return (duration && durationMap[duration]) || 0
}

export const lowestPriceForPlan = (plan: TODO) => {
  if (!!plan) {
    const values = [plan.fullPrice, plan.price, plan.salePrice].filter(
      (num) => num && typeof num === 'number'
    )

    return Math.min(...values)
  } else {
    return 0
  }
}
